<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import ReportHeader from './components/ReportHeader.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import WidgetLoadReportsAPI from 'dashboard/api/widgetLoadReports';
import WidgetReportFilters from './components/WidgetReportFilters.vue';
import { DEFAULT_BAR_CHART, DEFAULT_CHART } from './constants';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const i18nKey = 'WIDGET_LOAD_REPORTS';

const emptyMetrics = () => ({
  initialized: 0,
  total: 0,
  widget_loaded: 0,
  page_views: 0,
  opened: 0,
  closed: 0,
  messages_sent: 0,
  starter_clicks: 0,
  series: [],
});

const dateRange = ref([subDays(new Date(), 30), new Date()]);
const selectedInboxId = ref('');
const loading = ref(false);
const error = ref('');
const metrics = ref(emptyMetrics());

const websiteInboxes = useMapGetter('inboxes/getWebsiteInboxes');

const widgetOptions = computed(() =>
  [...(websiteInboxes.value || [])].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )
);

const persistInboxQuery = inboxId => {
  const nextQuery = { ...route.query };
  if (inboxId) {
    nextQuery.inbox_id = String(inboxId);
  } else {
    delete nextQuery.inbox_id;
  }
  if (route.query.inbox_id === nextQuery.inbox_id) return;
  if (!route.query.inbox_id && !inboxId) return;
  router.replace({ query: nextQuery });
};

const resolveInboxId = () => {
  const fromQuery = route.query.inbox_id;
  if (
    fromQuery &&
    widgetOptions.value.some(inbox => String(inbox.id) === String(fromQuery))
  ) {
    return String(fromQuery);
  }
  return '';
};

const fetchData = async () => {
  const [start, end] = dateRange.value;
  const params = {};
  if (start) params.since = start.toISOString();
  if (end) params.until = end.toISOString();
  if (selectedInboxId.value) params.inbox_id = selectedInboxId.value;

  loading.value = true;
  error.value = '';
  try {
    const { data } = await WidgetLoadReportsAPI.getMetrics(params);
    metrics.value = {
      ...emptyMetrics(),
      ...data,
      series: data.series || [],
    };
    if (data.error) {
      error.value = data.error;
    }
  } catch (e) {
    error.value = e.message || t(`${i18nKey}.ERROR`);
    metrics.value = emptyMetrics();
  } finally {
    loading.value = false;
  }
};

const handleDateChange = value => {
  dateRange.value = value;
  fetchData();
};

const handleInboxChange = inboxId => {
  selectedInboxId.value = inboxId == null ? '' : String(inboxId);
  persistInboxQuery(selectedInboxId.value);
  fetchData();
};

const summaryCards = computed(() => [
  {
    key: 'initialized',
    label: t(`${i18nKey}.SERVER_LOADS`),
    value: metrics.value.initialized ?? metrics.value.total ?? 0,
  },
  {
    key: 'widget_loaded',
    label: t(`${i18nKey}.WIDGET_LOADED`),
    value: metrics.value.widget_loaded || 0,
  },
  {
    key: 'page_views',
    label: t(`${i18nKey}.PAGE_VIEWS`),
    value: metrics.value.page_views || 0,
  },
  {
    key: 'opened',
    label: t(`${i18nKey}.OPENED`),
    value: metrics.value.opened || 0,
  },
  {
    key: 'messages_sent',
    label: t(`${i18nKey}.MESSAGES_SENT`),
    value: metrics.value.messages_sent || 0,
  },
  {
    key: 'starter_clicks',
    label: t(`${i18nKey}.STARTER_CLICKS`),
    value: metrics.value.starter_clicks || 0,
  },
]);

const hasSeries = computed(() => (metrics.value.series || []).length > 0);

const chartLabels = computed(() =>
  (metrics.value.series || []).map(row => {
    try {
      return format(parseISO(String(row.day).slice(0, 10)), 'dd-MMM');
    } catch {
      return String(row.day);
    }
  })
);

const chartCollection = computed(() => {
  const series = metrics.value.series || [];
  return {
    labels: chartLabels.value,
    datasets: [
      {
        ...DEFAULT_BAR_CHART,
        label: t(`${i18nKey}.SERVER_LOADS`),
        data: series.map(row => row.initialized || 0),
        borderRadius: 4,
        backgroundColor: 'rgb(31, 147, 255)',
      },
      {
        ...DEFAULT_BAR_CHART,
        label: t(`${i18nKey}.WIDGET_LOADED`),
        data: series.map(row => row.widget_loaded || 0),
        borderRadius: 4,
        backgroundColor: 'rgb(119, 155, 187)',
      },
      {
        ...DEFAULT_BAR_CHART,
        label: t(`${i18nKey}.PAGE_VIEWS`),
        data: series.map(row => row.page_views || 0),
        borderRadius: 4,
        backgroundColor: 'rgb(167, 139, 250)',
      },
      {
        ...DEFAULT_BAR_CHART,
        label: t(`${i18nKey}.OPENED`),
        data: series.map(row => row.opened || 0),
        borderRadius: 4,
        backgroundColor: 'rgb(61, 184, 143)',
      },
    ],
  };
});

const chartOptions = computed(() => ({
  scales: DEFAULT_CHART.scales,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
    },
  },
}));

watch(widgetOptions, options => {
  if (!options.length) return;
  const resolved = resolveInboxId();
  if (resolved !== selectedInboxId.value) {
    selectedInboxId.value = resolved;
  }
});

onMounted(async () => {
  await store.dispatch('inboxes/get');
  selectedInboxId.value = resolveInboxId();
  fetchData();
});
</script>

<template>
  <ReportHeader :header-title="t(`${i18nKey}.HEADER`)" />
  <div class="flex flex-col gap-4 pb-6">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-center print:hidden">
      <WootDatePicker
        :value="dateRange"
        :confirm-text="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        range
        @change="handleDateChange"
      />
      <WidgetReportFilters
        v-model="selectedInboxId"
        :widgets="widgetOptions"
        @update:model-value="handleInboxChange"
      />
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <span class="text-n-slate-11">{{ t(`${i18nKey}.LOADING_MESSAGE`) }}</span>
    </div>

    <template v-else>
      <div
        v-if="error"
        class="px-4 py-3 text-sm text-n-ruby-11 rounded-lg bg-n-ruby-3"
      >
        {{ error }}
      </div>

      <div
        class="grid grid-cols-2 md:grid-cols-3 gap-4 p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
      >
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="flex flex-col gap-1"
        >
          <span class="text-sm text-n-slate-11">{{ card.label }}</span>
          <span class="text-2xl font-semibold text-n-slate-12">{{
            card.value
          }}</span>
        </div>
      </div>

      <div
        class="p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
      >
        <h3 class="mb-4 text-base font-medium text-n-slate-12">
          {{ t(`${i18nKey}.DAILY_LOADS`) }}
        </h3>
        <div v-if="!hasSeries" class="py-8 text-sm text-center text-n-slate-10">
          {{ t(`${i18nKey}.NO_DATA`) }}
        </div>
        <div v-else class="h-72">
          <BarChart
            :collection="chartCollection"
            :chart-options="chartOptions"
          />
        </div>
      </div>
    </template>
  </div>
</template>
